import invariant from 'invariant';
import { Schema } from 'normalizr';
import conformsTo from 'lodash/conformsTo';
import {
  EntityKeyConfig,
  RequestData
} from '../../types/entities';
import {
  APH,
  NON_ENTITY_CALL,
  REQUEST_METHODS,
  ENTITY_KEY_TYPES,
  RequestMethod,
  EntityKeyType
} from './constants';

// Enhanced type definitions for better developer experience
export interface EntityClientOptions {
  data?: RequestData;
  mutateRequest?: (data: RequestData, query: Record<string, unknown>) => RequestData;
  mutateResponse?: (response: unknown, query: Record<string, unknown>) => unknown;
  resetCache?: boolean;
  disableErrorRedirect?: boolean;
  successNotification?: {
    type: 'success' | 'info' | 'warning' | 'error';
    message: string;
  };
  errorNotification?: {
    type: 'error';
    message: string;
  };
  deleteKey?: string;
  dependentDeletionKeys?: string[];
  [key: string]: unknown;
}

// Type-safe wrapper for common entity operations
export interface TypedEntityClient {
  (
    method: RequestMethod,
    entityKeyType: EntityKeyType | typeof NON_ENTITY_CALL,
    types: ActionTypes,
    url: string,
    options?: EntityClientOptions
  ): APHAction;
}

// Valid entity key types for compile-time and runtime validation
export type ValidEntityKeyType = keyof EntityKeyConfig | typeof NON_ENTITY_CALL;

// Runtime validation for entity key types
const VALID_ENTITY_KEY_TYPES: ReadonlySet<string> = new Set([
  ...Object.values(ENTITY_KEY_TYPES),
  NON_ENTITY_CALL
] as const);

/**
 * Validates that entityKeyType is one of the allowed values at runtime
 *
 * @param entityKeyType - The entity key type to validate
 * @throws Error if the entityKeyType is not one of the valid values
 */
function validateEntityKeyType(entityKeyType: string): asserts entityKeyType is EntityKeyType | typeof NON_ENTITY_CALL {
  if (!VALID_ENTITY_KEY_TYPES.has(entityKeyType)) {
    throw new Error(
      `Invalid entityKeyType: "${entityKeyType}". Must be one of: ${Array.from(VALID_ENTITY_KEY_TYPES).join(', ')}`
    );
  }
}

interface ActionTypes {
  REQUEST: string;
  SUCCESS: string;
  FAILURE: string;
}

interface StatusUpdate {
  isFetching?: number;
  isPerforming?: number;
  isConsolidatingFetch?: number;
  isConsolidatingPerform?: number;
}

// Legacy interface - kept for backward compatibility
interface QueryParams extends EntityClientOptions {}

interface APHAction {
  type: string;
  payload: {
    endpoint: string;
    method: string;
    data?: unknown;
    types: Array<{
      type: string;
      payload?: (action: unknown, state: unknown, response: unknown) => unknown;
      meta: () => Record<string, unknown>;
    }>;
  };
}

const typesShape = {
  REQUEST: (val: unknown): val is string => typeof val === 'string',
  SUCCESS: (val: unknown): val is string => typeof val === 'string',
  FAILURE: (val: unknown): val is string => typeof val === 'string',
};

const identity = (r: unknown): unknown => r;

const generateStatusUp = (fetching: boolean): StatusUpdate => ({
  ...(fetching && { isFetching: 1 }),
  ...(!fetching && { isPerforming: 1 }),
});

const generateStatusDown = (
  fetching: boolean,
  shouldPerform: boolean,
  shouldConsolidate: boolean
): StatusUpdate => ({
  ...(fetching && { isFetching: -1 }),
  ...(!fetching && { isPerforming: -1 }),
  ...(shouldPerform && fetching && shouldConsolidate &&
    { isConsolidatingFetch: 1 }),
  ...(shouldPerform && !fetching && shouldConsolidate &&
    { isConsolidatingPerform: 1 }),
});

const generateAPH = (
  schema: Schema | null,
  keys: EntityKeyConfig | null,
  method: RequestMethod,
  entityKeyType: EntityKeyType | typeof NON_ENTITY_CALL,
  types: ActionTypes,
  url: string,
  queryParams: QueryParams = {},
): APHAction => {
  // Validate entityKeyType at runtime for additional safety
  validateEntityKeyType(entityKeyType);
  const {
    data,
    mutateRequest = identity,
    mutateResponse = identity,
    resetCache,
    disableErrorRedirect,
    successNotification,
    errorNotification,
    // TODO: clean this up:
    deleteKey,
    dependentDeletionKeys = [],
    ...optionalParams
  } = queryParams;

  invariant(
    typeof method === 'string',
    '(modules/api/base): a string method type is required',
  );

  const shouldEntityExist = entityKeyType !== NON_ENTITY_CALL;

  if (schema && method !== REQUEST_METHODS.DELETE) {
    // More robust schema validation that works with minified production builds
    const schemaWithProps = schema as { key?: string; schema?: object };
    const isValidSchema = schema &&
      typeof schema === 'object' &&
      schema.constructor &&
      (schema.constructor.name === 'Entity' ||
       schema.constructor.name === 'EntitySchema2' ||
       // Fallback for minified builds - check if it has Entity-like properties
       (typeof schemaWithProps.key === 'string' && typeof schemaWithProps.schema === 'object'));

    invariant(
      keys &&
      entityKeyType !== NON_ENTITY_CALL &&
      typeof keys[entityKeyType as keyof EntityKeyConfig] === 'string' &&
      isValidSchema,
      '(modules/api/base): valid schema class and entityKey are required',
    );
  }

  invariant(
    conformsTo(types, typesShape),
    '(modules/api/base): a valid REQUEST/SUCCESS/FAILURE types object is required',
  );

  const fetching = method === REQUEST_METHODS.GET;
  const deleting = method === REQUEST_METHODS.DELETE;

  const baseMeta = {
    query: {
      ...optionalParams,
      ...(keys && entityKeyType !== NON_ENTITY_CALL && {
        entityKey: keys[entityKeyType as keyof EntityKeyConfig]
      }),
    },
    ...(disableErrorRedirect && { disableErrorRedirect: true }),
  };

  return {
    type: APH,
    payload: {
      endpoint: url,
      method,
      ...(!!data && { data: mutateRequest(data, baseMeta.query) }),
      types: [
        {
          type: types.REQUEST,
          meta: () => ({
            ...baseMeta,
            status: generateStatusUp(fetching),
          }),
        },
        {
          type: types.SUCCESS,
          payload: (action: unknown, state: unknown, response: unknown) =>
            mutateResponse(response, baseMeta.query),
          meta: () => ({
            ...baseMeta,
            status: generateStatusDown(fetching, true, shouldEntityExist),
            ...(schema && keys && entityKeyType !== NON_ENTITY_CALL && {
              schema,
              entityKey: keys[entityKeyType as keyof EntityKeyConfig],
            }),
            method,
            resetCache,
            ...(fetching && { cache: url }),
            ...(successNotification && { notification: successNotification }),
            ...(deleting && { deleteKey, dependentDeletionKeys }),
          }),
        },
        {
          type: types.FAILURE,
          meta: () => ({
            ...baseMeta,
            status: generateStatusDown(fetching, false, shouldEntityExist),
            ...(errorNotification && { notification: errorNotification }),
          }),
        },
      ],
    },
  };
};

/**
 * The base API function to call any endpoint with entity normalization
 *
 * @param schema - A normalizr Entity schema object for normalizing the response
 * @param keys - An object containing the entity keys (single, plural, state)
 * @param method - The HTTP request method (GET, POST, PATCH, DELETE)
 * @param entityKeyType - The entity key type (single or plural) for normalization
 * @param types - An object of action type strings (REQUEST/SUCCESS/FAILURE)
 * @param url - The endpoint URL for the API request
 * @param params - Optional parameters including data, mutations, notifications, and other options
 * @returns An FSA-compliant action object that can be dispatched to Redux
 */
const base = (
  schema: Schema,
  keys: EntityKeyConfig,
  method: RequestMethod,
  entityKeyType: EntityKeyType | typeof NON_ENTITY_CALL,
  types: ActionTypes,
  url: string,
  params: QueryParams = {}
): APHAction =>
  generateAPH(schema, keys, method, entityKeyType, types, url, params);

/**
 * The base non-entity API function to call any endpoint without normalization
 *
 * @param method - The HTTP request method (GET, POST, PATCH, DELETE)
 * @param types - An object of action type strings (REQUEST/SUCCESS/FAILURE)
 * @param url - The endpoint URL for the API request
 * @param params - Optional parameters including data, mutations, notifications, and other options
 * @returns An FSA-compliant action object that can be dispatched to Redux
 */
const nonEntityBase = (
  method: RequestMethod,
  types: ActionTypes,
  url: string,
  params: QueryParams = {}
): APHAction =>
  generateAPH(null, null, method, NON_ENTITY_CALL, types, url, params);

/**
 * Creates a bound API client function for a specific entity schema and keys
 *
 * @param schema - The normalizr schema for the entity
 * @param keys - The EntityKeyConfig containing the entity keys (single, plural, state)
 * @returns A typed function bound to this entity that can be used to make API calls
 */
const api = (schema: Schema, keys: EntityKeyConfig): TypedEntityClient =>
  base.bind(null, schema, keys) as TypedEntityClient;

/**
 * Creates standard CRUD action types for an entity (FETCH, CREATE, UPDATE, DELETE)
 *
 * @param entityName - The entity name (e.g., 'Profile', 'User')
 * @returns Object with REQUEST/SUCCESS/FAILURE action type strings for each CRUD operation
 */
export const createCRUDActionTypes = (entityName: string) => {
  const upperName = entityName.toUpperCase();
  return {
    FETCH: {
      REQUEST: `FETCH_${upperName}_REQUEST`,
      SUCCESS: `FETCH_${upperName}_SUCCESS`,
      FAILURE: `FETCH_${upperName}_FAILURE`,
    },
    CREATE: {
      REQUEST: `CREATE_${upperName}_REQUEST`,
      SUCCESS: `CREATE_${upperName}_SUCCESS`,
      FAILURE: `CREATE_${upperName}_FAILURE`,
    },
    UPDATE: {
      REQUEST: `UPDATE_${upperName}_REQUEST`,
      SUCCESS: `UPDATE_${upperName}_SUCCESS`,
      FAILURE: `UPDATE_${upperName}_FAILURE`,
    },
    DELETE: {
      REQUEST: `DELETE_${upperName}_REQUEST`,
      SUCCESS: `DELETE_${upperName}_SUCCESS`,
      FAILURE: `DELETE_${upperName}_FAILURE`,
    },
  };
};

/**
 * Creates a standard notification configuration object
 *
 * @param message - The notification message to display
 * @param type - The notification type (success, info, warning, error). Defaults to 'success'
 * @returns A properly typed notification configuration object
 */
export const createNotification = (
  message: string,
  type: 'success' | 'info' | 'warning' | 'error' = 'success'
) => ({ message, type });

/**
 * Creates an error notification configuration object
 *
 * @param message - The error message to display
 * @returns A properly typed error notification configuration object
 */
export const createErrorNotification = (message: string) => ({
  message,
  type: 'error' as const,
});

export { api };
export { nonEntityBase as nonEntityApi };